import {
  useQuery as useReactQuery,
  useInfiniteQuery as useReactInfiniteQuery,
  QueryFunctionContext,
  QueryFunction,
  UseQueryResult,
  UseInfiniteQueryResult,
  InfiniteData,
} from 'react-query';
import { queryClient } from '../../services';

export const useQuery = <TData>(
  key: string | any[],
  queryFn: QueryFunction<TData>,
  config?: Parameters<typeof useReactQuery>[2],
): UseQueryResult<TData> => {
  const globalBeforeFetch = async (): Promise<any> => {
    return queryClient.getQueryState(key);
  };

  const customQueryFn = async (context: QueryFunctionContext) => {
    const res = await globalBeforeFetch();
    if (res.dataUpdateCount !== 0 && res.status === 'success') return res.data;

    return queryFn(context);
  };

  return useReactQuery<any>(key, customQueryFn, { ...config });
};

type CustomInfiniteQueryResult<TData> = Omit<
  UseInfiniteQueryResult<InfiniteData<TData>>,
  'data'
> & {
  data: TData | undefined;
};

export const useInfiniteQuery = <TData>(
  key: string | any[],
  queryFn: QueryFunction<TData>,
  config?: Parameters<typeof useReactInfiniteQuery>[2],
): CustomInfiniteQueryResult<TData> => {
  const globalBeforeFetch = async (): Promise<any> => {
    return queryClient.getQueryState(key);
  };

  const customQueryFn = async (context: QueryFunctionContext) => {
    const res = await globalBeforeFetch();

    const pageParam = context.pageParam || 0;

    if (res.data?.pages[pageParam]) {
      return res.data.pages[pageParam];
    }

    return queryFn(context);
  };

  const result = useReactInfiniteQuery<any>(key, customQueryFn, { ...config });
  return {
    ...result,
    data: result?.data as unknown as TData,
  };
};
